import { type JsonValue } from 'type-fest';

import { isIterable } from './iterableUtils';

type HasToJSON = {
  toJSON(): JsonValue;
};
type HasEntries = {
  entries(): [unknown, unknown];
};

export const toJson = <T, J extends JsonValue>(v: T): J => {
  if (typeof v === undefined || typeof v === 'symbol') {
    //`undefined` and `Symbol()` are mapped to `null`. (JSON.stringify(Symbol()) === 'undefined')
    return null as J;
  }
  //Json primitives are mapped to self
  if (
    v === null ||
    typeof v === 'number' ||
    typeof v === 'string' ||
    typeof v === 'boolean'
  ) {
    return v as unknown as J;
  }
  if (typeof (v as Partial<HasToJSON>).toJSON === 'function') {
    //Trust and use the `toJSON` method
    return (v as unknown as HasToJSON).toJSON() as J;
  }
  if (typeof v === 'object') {
    if (Array.isArray(v)) {
      //recurse toJson for its elements
      return v.map(toJson) as J;
    }
    if (typeof (v as Partial<HasEntries>).entries === 'function') {
      //recurse toJson for its key/value pairs
      return Array.from((v as unknown as HasEntries).entries()).map(
        ([_k, _v]) => [toJson(_k), toJson(_v)],
      ) as J;
    }
    if (isIterable(v)) {
      //recurse toJson for its elements
      return Array.from(v).map(toJson) as J;
    }
    //Recurse toJson into its elements
    return Object.entries(v).map(
      (_k: unknown, _v: unknown) => [toJson(_k), toJson(_v)] as J,
    );
  }
  return null;
};
